import clsx from 'clsx';
import { Temporal } from 'temporal-polyfill';
import type {
  AttendancePolicyV2HistoryEventKind,
  AttendancePolicyV2HistorySubstituteRequest,
} from '@airreading/common/data';
import { Badge } from '@/components/ui/badge.js';
import {
  AttendancePolicyV2HistoryEventBadgeColorMap,
  AttendancePolicyV2HistoryEventDotColorMap,
} from '@/lib/attendance-policy-v2-display.js';
import { VerticalTimeline } from '@/components/vertical-timeline.js';

export const AttendancePolicyV2HistoryModalItem = ({
  kind,
  eventPlainDate,
  title,
  explanation,
  outcomeLabel,
  substituteRequests,
}: {
  kind: AttendancePolicyV2HistoryEventKind;
  eventPlainDate: Temporal.PlainDate;
  title: string;
  explanation: string | null;
  outcomeLabel: string;
  substituteRequests: AttendancePolicyV2HistorySubstituteRequest[];
}) => {
  return (
    <li className="flex items-start gap-4">
      <VerticalTimeline color={AttendancePolicyV2HistoryEventDotColorMap[kind]} />
      <div className="flex flex-1 min-w-0 items-center gap-4 border-b border-slate-200 py-2">
        <div className="text-xs min-w-20">
          {eventPlainDate.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </div>
        <div className="flex flex-col flex-1 justify-start">
          <span className="text-sm font-medium">{title}</span>
          {explanation ? <div className="text-xs text-muted-foreground whitespace-pre-wrap">{explanation}</div> : null}
          {substituteRequests.length > 0 ? (
            <ul className="mt-1 flex flex-col gap-1" data-testid="teachers-attendance-history-list-sub-requests">
              {substituteRequests.map((request) => (
                <AttendancePolicyV2HistorySubstituteRequestRow key={request.id} request={request} />
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex justify-start">
          <Badge
            className={clsx(
              'ml-auto shrink-0 p-1 rounded-[6px] text-[12px] font-bold',
              AttendancePolicyV2HistoryEventBadgeColorMap[kind],
            )}
          >
            {outcomeLabel}
          </Badge>
        </div>
      </div>
    </li>
  );
};

const SubstituteRequestStatusLabelMap: Record<AttendancePolicyV2HistorySubstituteRequest['status'], string> = {
  pending: 'Pending',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled',
};

const AttendancePolicyV2HistorySubstituteRequestRow = ({
  request,
}: {
  request: AttendancePolicyV2HistorySubstituteRequest;
}) => (
  <li
    className="text-xs text-muted-foreground"
    data-testid={`teachers-attendance-history-item-sub-request-${request.id}`}
  >
    <div>
      <span className="font-medium text-foreground">Requested by {request.requestedByCoach.fullNameOrEmail}</span>
      {' · '}
      {SubstituteRequestStatusLabelMap[request.status]}
    </div>
    <div className="whitespace-pre-wrap">
      Reason: {request.requestReason ?? <span className="italic">No reason recorded</span>}
    </div>
  </li>
);
